import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logoImage from '../assets/logo_small.png';
import industryDataEn from '../data/industryData.json';
import industryDataEs from '../data/industryData_es.json';

// Group industries by category
function groupByCategory(data: typeof industryDataEn) {
  return data.reduce<Record<string, typeof data>>((acc, ind) => {
    if (!acc[ind.category]) acc[ind.category] = [];
    acc[ind.category].push(ind);
    return acc;
  }, {});
}

const categoriesEn = groupByCategory(industryDataEn);
const categoriesEs = groupByCategory(industryDataEs);

interface MegaFooterProps {
  /** Pass scrollToSection when used from Home page for in-page nav */
  scrollToSection?: (id: string) => void;
  /** Locale for industry links — defaults to 'en' */
  locale?: 'en' | 'es';
}

export function MegaFooter({ scrollToSection, locale = 'en' }: MegaFooterProps) {
  const { i18n } = useTranslation();
  // Use a fixed translation function for the given locale so ALL footer text
  // renders in the correct language, even if the global i18next lang differs.
  const t = i18n.getFixedT(locale);
  const scrollTop = () => window.scrollTo(0, 0);
  const isEs = locale === 'es';
  const categories = isEs ? categoriesEs : categoriesEn;
  const pathPrefix = isEs ? '/es/soluciones' : '/solutions';
  const directoryLabel = isEs ? 'Soluciones por Industria' : 'Solutions by Industry';

  // Locale-aware paths for nav links
  const paths = isEs
    ? { features: '/#features', pricing: '/pricing', faq: '/#faq', privacy: '/es/privacidad', terms: '/es/terminos', cookies: '/es/cookies' }
    : { features: '/#features', pricing: '/pricing', faq: '/#faq', privacy: '/privacy', terms: '/terms', cookies: '/cookies' };

  return (
    <footer className="relative z-10 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">

      {/* ── Top: Branding + Product/Company/Legal columns ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src={logoImage} alt="Visto Logo" width="40" height="40" className="w-10 h-10 object-contain" />
              <span className="text-2xl font-extrabold text-slate-900 dark:text-white">Visto</span>
            </div>
            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-extrabold text-slate-900 dark:text-white mb-4">{t('footer.product')}</h4>
            <ul className="space-y-3 text-sm">
              {scrollToSection ? (
                <>
                  <li><button onClick={() => scrollToSection('features')} className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('nav.features')}</button></li>
                  <li><button onClick={() => scrollToSection('pricing')} className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('nav.pricing')}</button></li>
                  <li><button onClick={() => scrollToSection('features')} className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('footer.integrations')}</button></li>
                  <li><button onClick={() => scrollToSection('faq')} className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('footer.faq')}</button></li>
                </>
              ) : (
                <>
                  <li><Link to={paths.features} onClick={scrollTop} className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('nav.features')}</Link></li>
                  <li><Link to={paths.pricing} onClick={scrollTop} className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('nav.pricing')}</Link></li>
                  <li><Link to={paths.faq} onClick={scrollTop} className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('footer.faq')}</Link></li>
                </>
              )}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-extrabold text-slate-900 dark:text-white mb-4">{t('footer.company')}</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="https://calendar.app.google/vvEQCYQAiJemGrgr8" target="_blank" rel="noopener noreferrer" className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('footer.contact')}</a></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-extrabold text-slate-900 dark:text-white mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to={paths.privacy} onClick={scrollTop} className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('footer.privacy')}</Link></li>
              <li><Link to={paths.terms} onClick={scrollTop} className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('footer.terms')}</Link></li>
              <li><Link to={paths.cookies} onClick={scrollTop} className="text-slate-600 dark:text-slate-400 hover:text-emerald-500 transition-colors">{t('footer.cookies')}</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* ── Middle: Industry Directory (Mega Footer SEO Grid) ── */}
      <div className="border-t border-slate-200/70 dark:border-slate-800/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h4 className="text-xs font-extrabold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-6">
            {directoryLabel}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {Object.entries(categories).map(([category, industries]) => (
              <div key={category}>
                <h5 className="text-sm font-bold text-slate-900 dark:text-white mb-3">
                  {category}
                </h5>
                <ul className="space-y-2">
                  {industries.map((ind) => (
                    <li key={ind.id}>
                      <Link
                        to={`${pathPrefix}/${ind.id}`}
                        onClick={scrollTop}
                        className="text-sm text-slate-500 dark:text-slate-400 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors"
                      >
                        {ind.industryName}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bottom Bar ── */}
      <div className="border-t border-slate-200/70 dark:border-slate-800/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-400 dark:text-slate-500">
            <Link to={paths.privacy} onClick={scrollTop} className="hover:text-emerald-500 transition-colors">{t('footer.privacy')}</Link>
            <span>·</span>
            <Link to={paths.terms} onClick={scrollTop} className="hover:text-emerald-500 transition-colors">{t('footer.terms')}</Link>
            <span>·</span>
            <Link to={paths.cookies} onClick={scrollTop} className="hover:text-emerald-500 transition-colors">{t('footer.cookies')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
